import type {
  AuthResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  MessageResponse,
  RegisterRequest,
  RegisterResponse,
  ResendEmailVerificationRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
} from '../../types/api';
import type { StoredUser, User } from '../../types';
import { toStoredRegistrationConsents } from '../../types/consents';
import {
  createMockToken,
  delay,
  mockStorageKeys,
  parseMockToken,
  readJson,
  writeJson,
} from '../mock/storage';

type TokenMap = Record<string, string>;

function readTokenMap(key: string): TokenMap {
  return readJson<TokenMap>(key, {});
}

function writeTokenMap(key: string, value: TokenMap) {
  writeJson(key, value);
}

function toPublicUser(user: StoredUser): User {
  const { password: _, ...publicUser } = user;
  return {
    ...publicUser,
    phone: user.phone ?? null,
    role: user.role ?? 'customer',
    emailConfirmed: user.emailConfirmed ?? user.role === 'admin',
  };
}

export async function mockLogin(data: LoginRequest): Promise<AuthResponse> {
  await delay();

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const found = users.find(
    (u) => u.email.toLowerCase() === data.email.toLowerCase() && u.password === data.password,
  );

  if (!found) {
    throw new Error('E-posta veya şifre hatalı.');
  }

  if (found.role !== 'admin' && !found.emailConfirmed) {
    throw new Error('Giriş yapmadan önce e-posta adresinizi doğrulamanız gerekiyor.');
  }

  return { user: toPublicUser(found), token: createMockToken(found.id) };
}

export async function mockRegister(data: RegisterRequest): Promise<RegisterResponse> {
  await delay();

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const exists = users.some((u) => u.email.toLowerCase() === data.email.toLowerCase());

  if (exists) {
    throw new Error('Bu e-posta adresi zaten kayıtlı.');
  }

  if (!data.consents.termsAccepted || !data.consents.kvkkRead) {
    throw new Error('Üyelik için gerekli sözleşme onayları verilmelidir.');
  }

  const newUser: StoredUser = {
    id: crypto.randomUUID(),
    name: data.name.trim(),
    email: data.email.toLowerCase().trim(),
    password: data.password,
    role: 'customer',
    emailConfirmed: false,
    registrationConsents: toStoredRegistrationConsents(data.consents),
  };

  writeJson(mockStorageKeys.users, [...users, newUser]);

  const verificationToken = crypto.randomUUID();
  const verificationTokens = readTokenMap(mockStorageKeys.emailVerificationTokens);
  verificationTokens[verificationToken] = newUser.id;
  writeTokenMap(mockStorageKeys.emailVerificationTokens, verificationTokens);

  return {
    message:
      'Kayıt başarılı. Lütfen e-posta adresinizi doğrulamak için gelen kutunuzu kontrol edin.',
    requiresEmailVerification: true,
    user: toPublicUser(newUser),
  };
}

export async function mockVerifyEmail(token: string): Promise<MessageResponse> {
  await delay();

  const verificationTokens = readTokenMap(mockStorageKeys.emailVerificationTokens);
  const userId = verificationTokens[token];

  if (!userId) {
    throw new Error('Geçersiz veya süresi dolmuş doğrulama bağlantısı.');
  }

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const index = users.findIndex((u) => u.id === userId);

  if (index === -1) {
    throw new Error('Geçersiz veya süresi dolmuş doğrulama bağlantısı.');
  }

  if (users[index].emailConfirmed) {
    return {
      message: 'E-posta adresiniz zaten doğrulanmış. Giriş yapabilirsiniz.',
    };
  }

  users[index] = { ...users[index], emailConfirmed: true };
  writeJson(mockStorageKeys.users, users);

  delete verificationTokens[token];
  writeTokenMap(mockStorageKeys.emailVerificationTokens, verificationTokens);

  return {
    message: 'E-posta adresiniz başarıyla doğrulandı. Artık giriş yapabilirsiniz.',
  };
}

export async function mockResendVerificationEmail(
  data: ResendEmailVerificationRequest,
): Promise<MessageResponse> {
  await delay();

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const user = users.find((u) => u.email.toLowerCase() === data.email.toLowerCase().trim());

  if (user && !user.emailConfirmed) {
    const verificationToken = crypto.randomUUID();
    const verificationTokens = readTokenMap(mockStorageKeys.emailVerificationTokens);
    verificationTokens[verificationToken] = user.id;
    writeTokenMap(mockStorageKeys.emailVerificationTokens, verificationTokens);
  }

  return {
    message: 'Doğrulama e-postası gönderildi (kayıtlı ve doğrulanmamışsa).',
  };
}

export async function mockForgotPassword(data: ForgotPasswordRequest): Promise<MessageResponse> {
  await delay();

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const user = users.find((u) => u.email.toLowerCase() === data.email.toLowerCase().trim());

  if (user) {
    const resetToken = crypto.randomUUID();
    const resetTokens = readTokenMap(mockStorageKeys.passwordResetTokens);
    resetTokens[resetToken] = user.id;
    writeTokenMap(mockStorageKeys.passwordResetTokens, resetTokens);
  }

  return {
    message:
      'E-posta adresiniz kayıtlıysa şifre sıfırlama bağlantısı gönderildi. Lütfen gelen kutunuzu kontrol edin.',
  };
}

export async function mockResetPassword(data: ResetPasswordRequest): Promise<MessageResponse> {
  await delay();

  const resetTokens = readTokenMap(mockStorageKeys.passwordResetTokens);
  const userId = resetTokens[data.token];

  if (!userId) {
    throw new Error('Geçersiz veya süresi dolmuş şifre sıfırlama bağlantısı.');
  }

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const index = users.findIndex((u) => u.id === userId);

  if (index === -1) {
    throw new Error('Geçersiz veya süresi dolmuş şifre sıfırlama bağlantısı.');
  }

  users[index] = { ...users[index], password: data.newPassword };
  writeJson(mockStorageKeys.users, users);

  delete resetTokens[data.token];
  writeTokenMap(mockStorageKeys.passwordResetTokens, resetTokens);

  return { message: 'Şifreniz başarıyla güncellendi. Yeni şifrenizle giriş yapabilirsiniz.' };
}

export async function mockLogout(): Promise<void> {
  await delay(150);
}

export async function mockGetCurrentUser(token: string | null): Promise<User | null> {
  if (!token) return null;

  const userId = parseMockToken(token);
  if (!userId) return null;

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const found = users.find((u) => u.id === userId);
  if (!found) return null;

  return toPublicUser(found);
}

export async function mockUpdateProfile(
  token: string | null,
  data: UpdateProfileRequest,
): Promise<User> {
  await delay();

  if (!token) {
    throw new Error('Oturum bulunamadı.');
  }

  const userId = parseMockToken(token);
  if (!userId) {
    throw new Error('Oturum bulunamadı.');
  }

  const hasName = data.name !== undefined;
  const hasPhone = data.phone !== undefined;
  if (!hasName && !hasPhone) {
    throw new Error('Güncellenecek en az bir alan gönderilmelidir.');
  }

  if (hasName && !data.name?.trim()) {
    throw new Error('Ad soyad boş olamaz.');
  }

  if (hasPhone && data.phone) {
    const phone = data.phone.trim();
    if (phone.length < 7 || phone.length > 30) {
      throw new Error('Telefon numarası 7 ile 30 karakter arasında olmalıdır.');
    }
  }

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const index = users.findIndex((u) => u.id === userId);
  if (index === -1) {
    throw new Error('Kullanıcı bulunamadı.');
  }

  const nextUser: StoredUser = { ...users[index] };
  if (hasName) {
    nextUser.name = data.name!.trim();
  }
  if (hasPhone) {
    const phone = data.phone?.trim();
    nextUser.phone = phone ? phone : null;
  }

  users[index] = nextUser;
  writeJson(mockStorageKeys.users, users);

  return toPublicUser(nextUser);
}

export async function mockChangePassword(
  token: string | null,
  data: ChangePasswordRequest,
): Promise<MessageResponse> {
  await delay();

  if (!token) {
    throw new Error('Oturum bulunamadı.');
  }

  const userId = parseMockToken(token);
  if (!userId) {
    throw new Error('Oturum bulunamadı.');
  }

  if (data.newPassword.length < 6) {
    throw new Error('Yeni şifre en az 6 karakter olmalıdır.');
  }

  if (data.currentPassword === data.newPassword) {
    throw new Error('Yeni şifre mevcut şifreden farklı olmalıdır.');
  }

  const users = readJson<StoredUser[]>(mockStorageKeys.users, []);
  const index = users.findIndex((u) => u.id === userId);
  if (index === -1) {
    throw new Error('Kullanıcı bulunamadı.');
  }

  if (users[index].password !== data.currentPassword) {
    throw new Error('Mevcut şifre hatalı.');
  }

  users[index] = { ...users[index], password: data.newPassword };
  writeJson(mockStorageKeys.users, users);

  return { message: 'Şifreniz başarıyla güncellendi.' };
}
